import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { GlossaryDataType, JuriContactType, JuriLinkType, JurisdictionType, PermittingDocumentType, WikiJurisdictionType, WikiNoteType } from '../../api/jurisdiction';

export type ActiveTabType = 'I' | 'P' | 'N' | 'None';
export type WikiModeType = 'JURI' | 'GLOSSARY' | 'KEYS';
// Option object from the State dropdown (see statesItems in WikiModule/wikiutil.ts).
export type WikiStateOption = {
  id: string;
  name?: string;
  title?: string;
  label?: string;
  value?: string;
};
export interface WikiState {
  addJuristdictionOpen: boolean;
  addGlossaryOpen: boolean;
  jurisdictions: any[];
  selectedState: WikiStateOption;
  selectedJurisdiction: WikiJurisdictionType;
  selectedContact: JuriContactType;
  selectedLink: JuriLinkType;
  selectedGlossary: GlossaryDataType;
  activeTab: ActiveTabType;
  permittingDoc: PermittingDocumentType;
  permittingPristine: boolean;
  permittingDocErrors: any;
  wikiNote: WikiNoteType;
  wikiMode: WikiModeType;
  previewImage: string;
}

const initialState: WikiState = {
  addJuristdictionOpen: false,
  addGlossaryOpen: false,
  jurisdictions: [],
  selectedState: { id: 'FL', name: 'Florida', title: 'Florida' },
  selectedJurisdiction: {} as WikiJurisdictionType,
  selectedContact: {} as JuriContactType,
  selectedLink: {} as JuriLinkType,
  activeTab: 'None',
  permittingDoc: {} as PermittingDocumentType,
  permittingPristine: true,
  permittingDocErrors: {},
  wikiNote: {} as WikiNoteType,
  wikiMode: 'JURI',
  previewImage: '',
  selectedGlossary: {} as GlossaryDataType,
};

export const wikiSlice = createSlice({
  name: 'wiki',
  initialState,
  reducers: {
    setAddJuristdictionOpen: (state, action: PayloadAction<boolean>) => {
      state.addJuristdictionOpen = action.payload;
    },
    setAddGlossaryOpen: (state, action: PayloadAction<boolean>) => {
      state.addGlossaryOpen = action.payload;
    },
    setJurisdictions: (state, action: PayloadAction<any[]>) => {
      state.jurisdictions = action.payload;
    },
    setSelectedState: (state, action: PayloadAction<WikiStateOption>) => {
      state.selectedState = action.payload;
    },
    setSelectedJurisdiction: (state, action: PayloadAction<WikiJurisdictionType>) => {
      state.selectedJurisdiction = action.payload;
    },
    setSelectedContact: (state, action: PayloadAction<JuriContactType>) => {
      state.selectedContact = action.payload;
    },
    setSelectedLink: (state, action: PayloadAction<JuriLinkType>) => {
      state.selectedLink = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<ActiveTabType>) => {
      state.activeTab = action.payload;
    },
    setPermittingDoc: (state, action: PayloadAction<PermittingDocumentType>) => {
      state.permittingDoc = action.payload;
    },
    setPermittingPristine: (state, action: PayloadAction<boolean>) => {
      state.permittingPristine = action.payload;
    },
    setPermittingDocErrors: (state, action: PayloadAction<any>) => {
      state.permittingDocErrors = action.payload;
    },
    setWikiNote: (state, action: PayloadAction<WikiNoteType>) => {
      state.wikiNote = action.payload;
    },
    setWikiMode: (state, action: PayloadAction<WikiModeType>) => {
      state.wikiMode = action.payload;
    },
    setPreviewImage: (state, action: PayloadAction<string>) => {
      state.previewImage = action.payload;
    },
    setSelectedGlossary: (state, action: PayloadAction<GlossaryDataType>) => {
      state.selectedGlossary = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setAddJuristdictionOpen,
  setAddGlossaryOpen,
  setJurisdictions,
  setSelectedState,
  setSelectedJurisdiction,
  setSelectedContact,
  setSelectedLink,
  setActiveTab,
  setPermittingDoc,
  setPermittingPristine,
  setPermittingDocErrors,
  setWikiNote,
  setWikiMode,
  setPreviewImage,
  setSelectedGlossary,
} = wikiSlice.actions;

export default wikiSlice.reducer;
